import React, { useCallback, useMemo } from 'react';

/* eslint-disable max-statements */

import { openArtifactDisplay } from '../../context/ArtifactDisplayContext';
import type { PhaseBuildExecutionState } from '../../services/buildPhaseService';
import { formatPhaseCredits } from '../../utils/phaseBuildPresentation';
import { TaskChecklist } from '../TaskChecklist';

import {
  cascadePhaseTaskSelection,
  isSelectablePhaseTask,
  phaseTaskOptions,
  sanitizePhaseTaskSelection,
  usePhaseTaskSelection,
} from './phaseTaskSelectionStore';

const phaseTaskStatus = (status: string, selectable: boolean): string => {
  if (selectable && (status === 'todo' || status === 'backlog')) return 'Ready';

  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const PhaseTaskPicker: React.FC<{
  entryId: string;
  onSelectionChange?: (selectedTaskIds: string[]) => void;
  projectId?: string | null;
  state: PhaseBuildExecutionState;
}> = ({ onSelectionChange, projectId, state }) => {
  const phase = state.current_phase;
  const tasks = useMemo(() => phaseTaskOptions(state), [state]);
  const selection = usePhaseTaskSelection(projectId, phase?.id, tasks);
  const selectedTasks = useMemo(() => {
    const selected = new Set(sanitizePhaseTaskSelection(tasks, selection.selectedIds));

    return tasks.filter((task) => isSelectablePhaseTask(task) && selected.has(task.id));
  }, [selection.selectedIds, tasks]);
  const selectedCredits = selectedTasks.reduce((total, task) => total + task.estimated_credits, 0);
  const viewTask = useCallback((taskId: string) => {
    openArtifactDisplay({ tab: 'tasks', itemId: taskId, source: 'timeline' });
  }, []);

  if (!projectId || !phase || !selection.active) return null;

  return (
    <TaskChecklist ariaLabel={`Phase ${phase.phase_number} task selection`} heading="Tasks to run now"
      totals={`${selectedTasks.length} selected · ${formatPhaseCredits(selectedCredits)} credits`}
      items={tasks.map((task) => ({
        id: task.id, title: task.title, status: task.status,
        statusLabel: phaseTaskStatus(task.status, isSelectablePhaseTask(task)),
        detail: `${formatPhaseCredits(task.estimated_credits)} cr`,
        selectable: isSelectablePhaseTask(task),
        checked: isSelectablePhaseTask(task) && selection.selectedIds.includes(task.id),
      }))}
      onSelectionChange={(taskId, checked) => {
        const nextIds = cascadePhaseTaskSelection(tasks, selection.selectedIds, taskId, checked);

        selection.setSelectedIds(nextIds);
        onSelectionChange?.(nextIds);
      }} onViewTask={viewTask} />
  );
};
